import React from "react";
import { Link } from "react-router-dom";

import Seo from "../seo/Seo";
import {
  organizationSchema,
  webPageSchema,
  breadcrumbSchema,
} from "../seo/schema";
import PageHeader from "../components/PageHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import { CONTACT_EMAIL, SITE_NAME, SITE_URL } from "../data/site";
import { useNowIST } from "../lib/useResults";

const PATH = "/contact-us";

const Contact = () => {
  const nowIST = useNowIST();

  const title = "Contact Bagad Satta Company — Corrections & Support";
  const description = `Contact ${SITE_NAME} about a wrong result, a missing chart entry, a takedown request or any other question. We read every message.`;

  const trail = [
    { name: "Home", path: "/" },
    { name: "Contact Us", path: PATH },
  ];

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": SITE_URL + PATH + "#contact",
    url: SITE_URL + PATH,
    name: title,
    description,
    mainEntity: {
      "@type": "Organization",
      "@id": SITE_URL + "/#organization",
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: CONTACT_EMAIL,
          availableLanguage: ["Hindi", "English"],
          areaServed: "IN",
        },
      ],
    },
  };

  return (
    <>
      <Seo
        path={PATH}
        title={title}
        description={description}
        keywords="contact bagad satta company, satta result correction, report wrong result"
        schema={[
          organizationSchema(),
          webPageSchema({ path: PATH, title, description }),
          breadcrumbSchema(trail),
          contactSchema,
        ]}
      />

      <div className="page-banner">
        <div className="container page-padding">
          <Breadcrumbs trail={trail} />

          <PageHeader
            h1="Contact Us"
            intro="Spotted a wrong number or a gap in a chart? Tell us and we will check it."
            nowIST={nowIST}
          />

          <section className="card text-content">
            <h2>Email</h2>
            <p>
              Write to{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We read
              messages in Hindi and English.
            </p>

            <h2>Reporting a Wrong Result</h2>
            <p>
              Accuracy matters more to us than speed. If a published number
              looks wrong, please include these three things so we can verify it
              quickly:
            </p>
            <ul>
              <li>The game name — for example Bagad, Gali or Faridabad.</li>
              <li>The exact date of the result in question.</li>
              <li>The number you believe is correct, and where you saw it.</li>
            </ul>

            <h2>Other Requests</h2>
            <p>
              Takedown requests, corrections to any page, and questions about
              what we publish are all handled at the same address. For questions
              about how we handle data, see our{" "}
              <Link to="/privacy-policy">privacy policy</Link>.
            </p>

            <h2>Before You Write</h2>
            <p>
              Most questions — result timings, what XX means, how far back the
              archive runs — are already answered on the{" "}
              <Link to="/faq">FAQ page</Link>.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default Contact;
